/**
 * @fileoverview System-owned dashboard templates grid (`SystemDashboardsPage`) for `system-web`.
 * Migrated to the canonical DRY `GridPageShell` standard with `liveQuery`, shared badges
 * (`StatusBadge`, `LibraryBadge`, `VisibilityBadge`), shared `actionBuilders`,
 * `ReportDefinitionDetailPanel` slide-in, and local-first visibility actions.
 * @module system-web/pages/reports/SystemDashboardsPage
 *
 * Responsibilities: - Subscribe via `liveQuery` to system dashboard definitions (`type: 'dashboard'`)
 *                     in `getSystemDb()` (closes gap G-45).
 *                   - Render shared `StatusBadge` and `VisibilityBadge` (closes gap G-52).
 *                   - Provide standard `makeViewAction`, `makeEditAction`, `makeDuplicateAction`,
 *                     `makeArchiveAction`, `makeDeleteAction` plus `Full-Screen`, `Publish`,
 *                     `Add to Library` / `Remove from Library` (`toggleLibrary`), and
 *                     `Visibility` (`All Departments` / `Specific Tenants` / `System Only`).
 *                   - Retire `SeedToTenantModal` / `seedToTenant` (`apiFetch`) in favor of
 *                     local-first library visibility (closes gap G-43).
 * Dependencies:     - @firesquirrel/ui-admin (GridPageShell, StatusBadge, VisibilityBadge,
 *                     NewDashboardEntryModal, ReportDefinitionDetailPanel, makeViewAction,
 *                     makeEditAction, makeDuplicateAction, makeArchiveAction, makeDeleteAction,
 *                     makeArchiveSelectedAction, makeDeleteSelectedAction, useLiveSelection)
 *                   - ../../services/systemReportDefinitionService (Dexie reads + mutations)
 * Architecture:     - Local-first: `liveQuery` over `getSystemDb()`; zero direct API calls.
 *
 * @localFirst
 * Source of Truth: local (Dexie `report_definitions` in `firesquirrel-system`)
 * Write Flow: Dexie write via `systemReportDefinitionService` → `wakeLocalWrite('report_definitions')` → background sync
 * Sync Strategy: Optimistic write; background replication
 * Conflict Resolution: Last-write-wins (`updatedAt`)
 *
 * Notes:            - Closes gaps G-42, G-43, G-45, G-49, and G-52.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Plus,
  Globe,
  Lock,
  Send,
  Maximize2,
  Users,
  EyeOff,
} from 'lucide-react';
import {
  GridPageShell,
  StatusBadge,
  VisibilityBadge,
  NewDashboardEntryModal,
  ReportDefinitionDetailPanel,
  makeViewAction,
  makeEditAction,
  makeDuplicateAction,
  makeArchiveAction,
  makeDeleteAction,
  makeArchiveSelectedAction,
  makeDeleteSelectedAction,
  useLiveSelection,
  formatParcelDate as formatDate,
  type DataGridColumn,
  type DataGridFilter,
  type DataGridRowAction,
  type BulkAction,
  type DashboardCreationMethod,
} from '@firesquirrel/ui-admin';
import type { ReportDefinitionLocal, SystemLibraryVisibility } from '@firesquirrel/local-db';
import {
  watchReportDefinitions,
  publishReportDefinition,
  archiveReportDefinition,
  duplicateReportDefinition,
  deleteReportDefinition,
  toggleLibrary,
  setReportDefinitionVisibility,
} from '../../services/systemReportDefinitionService';
import { ConfirmActionModal } from '../../components/ui';
import {
  SYSTEM_DASHBOARDS_HELP_SECTIONS,
  SYSTEM_DASHBOARDS_TOUR_STEPS,
} from './systemDashboardsHelpContent';

const columns: DataGridColumn<ReportDefinitionLocal>[] = [
  {
    id: 'name',
    header: 'Dashboard Name',
    accessorKey: 'name',
    sortable: true,
    filterable: true,
    minWidth: 200,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          {row.description && <div className="text-sm text-gray-500">{row.description}</div>}
        </div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    sortable: true,
    filterable: true,
    width: 110,
    cell: (info) => <StatusBadge status={info.getValue() as string} />,
  },
  {
    id: 'visibility',
    header: 'Visibility',
    accessorFn: (row) => (row.isInLibrary ? (row.visibility ?? 'all-departments') : 'not-in-library'),
    sortable: true,
    filterable: true,
    width: 170,
    cell: (info) => {
      const row = info.row.original;
      return (
        <VisibilityBadge
          isInLibrary={Boolean(row.isInLibrary)}
          visibility={row.visibility ?? 'all-departments'}
          tenantCount={row.allowedTenantIds?.length ?? 0}
        />
      );
    },
  },
  {
    id: 'widgets',
    header: 'Widgets',
    accessorFn: (row) => row.dashboardConfig?.widgets?.length ?? 0,
    sortable: true,
    width: 90,
    cell: (info) => info.row.original.dashboardConfig?.widgets?.length ?? 0,
  },
  {
    id: 'scope',
    header: 'Scope',
    accessorFn: (row) => row.scope?.level ?? 'system',
    sortable: true,
    width: 100,
    cell: (info) => (
      <span className="text-xs font-medium capitalize text-gray-700">
        {info.row.original.scope?.level === 'system' ? 'System' : 'Dept'}
      </span>
    ),
  },
  {
    id: 'updatedAt',
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    sortable: true,
    width: 140,
    cell: (info) => {
      const val = info.getValue() as string;
      return val ? formatDate(val) : '—';
    },
  },
];

const filters: DataGridFilter[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'status',
    options: [
      { value: 'draft', label: 'Draft', color: '#6b7280' },
      { value: 'published', label: 'Published', color: '#16a34a' },
      { value: 'archived', label: 'Archived', color: '#d97706' },
    ],
  },
  {
    id: 'visibility',
    label: 'Visibility',
    type: 'status',
    options: [
      { value: 'all-departments', label: 'All Departments', color: '#16a34a' },
      { value: 'specific-tenants', label: 'Specific Tenants', color: '#2563eb' },
      { value: 'system-only', label: 'System Only', color: '#4b5563' },
      { value: 'not-in-library', label: 'Not in Library', color: '#9ca3af' },
    ],
  },
];

/**
 * @component SystemDashboardsPage
 * Purpose:     Manage system-scope dashboard templates and System Library visibility in `system-web`.
 * Props:       (none — route component at `/dashboard-templates`).
 * Behavior:    `liveQuery`-backed grid; slide-in detail panel (`ReportDefinitionDetailPanel`);
 *              local-first library exposure and visibility actions.
 * Data Source: local (`getSystemDb()` via `systemReportDefinitionService`).
 * Constraints: no direct API calls; `SeedToTenantModal` retired.
 * Performance: renders from cached Dexie state.
 */
export default function SystemDashboardsPage() {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<ReportDefinitionLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ReportDefinitionLocal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReportDefinitionLocal | null>(null);
  const [entryModalOpen, setEntryModalOpen] = useState(false);

  // Local-first subscription — watchReportDefinitions reads the raw
  // `report_definitions` table inside liveQuery's observation zone.
  useEffect(() => {
    const sub = watchReportDefinitions({ type: 'dashboard' }, {
      next: (data) => {
        setDashboards(data);
        setLoading(false);
      },
      error: (err) => {
        console.error('[SystemDashboardsPage] liveQuery error:', err);
        setLoading(false);
      },
    });
    return () => sub.unsubscribe();
  }, []);

  useLiveSelection(dashboards, selected, setSelected);

  const handleDuplicate = useCallback(async (row: ReportDefinitionLocal) => {
    await duplicateReportDefinition(row._id);
  }, []);

  const handleArchive = useCallback(async (row: ReportDefinitionLocal) => {
    await archiveReportDefinition(row._id);
  }, []);

  const handlePublish = useCallback(async (row: ReportDefinitionLocal) => {
    await publishReportDefinition(row._id);
  }, []);

  const handleToggleLibrary = useCallback(async (row: ReportDefinitionLocal) => {
    await toggleLibrary(row._id, !row.isInLibrary);
  }, []);

  const handleSetVisibility = useCallback(
    async (row: ReportDefinitionLocal, vis: SystemLibraryVisibility) => {
      await setReportDefinitionVisibility(row._id, vis, row.allowedTenantIds);
    },
    [],
  );

  const handleCreationMethodSelect = useCallback(
    async (method: DashboardCreationMethod) => {
      if (method === 'duplicate') {
        const target = selected ?? dashboards[0];
        if (target) {
          const copy = await duplicateReportDefinition(target._id);
          if (copy?._id) {
            navigate(`/dashboard-templates/${copy._id}`);
            return;
          }
        }
      }
      navigate('/dashboard-templates/new');
    },
    [navigate, selected, dashboards],
  );

  const rowActions: DataGridRowAction<ReportDefinitionLocal>[] = useMemo(
    () => [
      makeViewAction<ReportDefinitionLocal>((row) =>
        navigate(`/dashboard-templates/${row._id}/view`),
      ),
      {
        id: 'fullscreen',
        label: 'Full-Screen',
        icon: Maximize2,
        onClick: (row) => navigate(`/dashboard-templates/${row._id}/fullscreen`),
      },
      makeEditAction<ReportDefinitionLocal>((row) =>
        navigate(`/dashboard-templates/${row._id}`),
      ),
      makeDuplicateAction<ReportDefinitionLocal>(handleDuplicate),
      {
        id: 'publish',
        label: 'Publish',
        icon: Send,
        onClick: (row) => void handlePublish(row),
        hidden: (row) => row.status !== 'draft',
      },
      {
        id: 'addToLibrary',
        label: 'Add to Library',
        icon: Globe,
        onClick: (row) => void handleToggleLibrary(row),
        hidden: (row) =>
          row.scope?.level !== 'system' || row.status !== 'published' || Boolean(row.isInLibrary),
      },
      {
        id: 'removeFromLibrary',
        label: 'Remove from Library',
        icon: Lock,
        onClick: (row) => void handleToggleLibrary(row),
        hidden: (row) => row.scope?.level !== 'system' || !row.isInLibrary,
      },
      {
        id: 'vis-all',
        label: 'Visibility: All Departments',
        icon: Globe,
        onClick: (row) => void handleSetVisibility(row, 'all-departments'),
        hidden: (row) => !row.isInLibrary || (row.visibility ?? 'all-departments') === 'all-departments',
      },
      {
        id: 'vis-specific',
        label: 'Visibility: Specific Tenants',
        icon: Users,
        onClick: (row) => void handleSetVisibility(row, 'specific-tenants'),
        hidden: (row) => !row.isInLibrary || row.visibility === 'specific-tenants',
      },
      {
        id: 'vis-system',
        label: 'Visibility: System Only',
        icon: EyeOff,
        onClick: (row) => void handleSetVisibility(row, 'system-only'),
        hidden: (row) => !row.isInLibrary || row.visibility === 'system-only',
      },
      makeArchiveAction<ReportDefinitionLocal>(handleArchive, {
        hidden: (row) => row.status === 'archived',
      }),
      makeDeleteAction<ReportDefinitionLocal>((row) => setDeleteTarget(row)),
    ],
    [
      navigate,
      handleDuplicate,
      handlePublish,
      handleToggleLibrary,
      handleSetVisibility,
      handleArchive,
    ],
  );

  const bulkActions = useMemo<BulkAction<ReportDefinitionLocal>[]>(
    () => [
      makeArchiveSelectedAction<ReportDefinitionLocal>(async (rows) => {
        for (const r of rows) {
          if (r.status !== 'archived') await archiveReportDefinition(r._id);
        }
      }),
      makeDeleteSelectedAction<ReportDefinitionLocal>(async (rows) => {
        for (const r of rows) {
          await deleteReportDefinition(r._id);
        }
      }),
    ],
    [],
  );

  if (loading) return null;

  return (
    <>
      <GridPageShell<ReportDefinitionLocal>
        gridId="system-dashboards"
        gridType="dashboards"
        title="Dashboard Templates"
        subtitle={`${dashboards.length} ${dashboards.length === 1 ? 'template' : 'templates'}`}
        columns={columns}
        data={dashboards}
        getRowId={(row) => row._id}
        searchPlaceholder="Search dashboard templates..."
        filters={filters}
        toolbarActions={
          <button
            type="button"
            data-testid="new-system-dashboard-btn"
            onClick={() => setEntryModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            New Dashboard Template
          </button>
        }
        exportable
        emptyIcon={LayoutDashboard}
        emptyTitle="No dashboard templates"
        emptySubtitle="Create your first system-level dashboard template"
        emptyCTA={{
          label: 'New Dashboard Template',
          onClick: () => setEntryModalOpen(true),
        }}
        rowActions={rowActions}
        onRowClick={(row) => setSelected(row)}
        selectable
        bulkActions={bulkActions}
        panelContent={
          selected ? (
            <ReportDefinitionDetailPanel
              definition={selected}
              runLabel="View Dashboard"
              onRun={() => navigate(`/dashboard-templates/${selected._id}/view`)}
            />
          ) : undefined
        }
        panelTitle={selected?.name}
        onOpenFullPage={
          selected ? () => navigate(`/dashboard-templates/${selected._id}/view`) : undefined
        }
        onPanelEdit={selected ? () => navigate(`/dashboard-templates/${selected._id}`) : undefined}
        onPanelClose={() => setSelected(null)}
        helpSections={SYSTEM_DASHBOARDS_HELP_SECTIONS}
        tourSteps={SYSTEM_DASHBOARDS_TOUR_STEPS}
      />

      <NewDashboardEntryModal
        open={entryModalOpen}
        onClose={() => setEntryModalOpen(false)}
        showSystemLibraryOption={false}
        onSelect={(method) => void handleCreationMethodSelect(method)}
      />

      {deleteTarget && (
        <ConfirmActionModal
          title="Delete Dashboard Template?"
          description={
            <>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action
              cannot be undone.
            </>
          }
          confirmButtonLabel="Delete"
          variant="danger"
          onConfirm={async () => {
            await deleteReportDefinition(deleteTarget._id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
